import { SxProps, Theme } from '@mui/material/styles';
import { ErrorBoundary } from '@sentry/react';

import React, { useState, useEffect, useRef, useContext } from 'react';
import { useImmer } from 'use-immer';

import { ApiContext } from '_lib/api/apiContext';
import { AiCallForCallPopDTO, Company, isServiceTitanApiCompany, ServiceTitanMappedCall } from '_lib/api/apiTypes.shim';
import { useQueryClient } from '_lib/api/QueryProvider';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Box } from '_lib/components/Box/Box';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { Stack } from '_lib/components/Stack/Stack';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';

import { ThreeTabFooter } from '_lib/featureComponents/ThreeTabFooter/ThreeTabFooter';
import { TwoTabFooter } from '_lib/featureComponents/TwoTabFooter/TwoTabFooter';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { useAppDispatch, useAppSelector, useAppStore } from '_lib/redux/hooks';
import { VerticalDivider } from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { getTradeForNewCompany } from '_lib/utils/helpers';
import { logDebug } from '_lib/utils/logging';

import { reportException } from '_lib/utils/sentryService';

import { useOurSnackbar } from '_lib/utils/snackUtils';

import { StApiCompanyContext } from '../../common/CompanyPane/Company.slice';
import { CompanyPane } from '../../common/CompanyPane/CompanyPane';

import { AppointmentBookingPane } from './featureComponents/AppointmentBookingPane/AppointmentBookingPane';
import { BookingState } from './featureComponents/AppointmentBookingPane/AppointmentBookingPane.types';
import { createBookingState } from './featureComponents/AppointmentBookingPane/AppointmentBookingPaneUtils';
import { CallDetailsPane } from './featureComponents/CallDetailsPane/CallDetailsPane';
import { CompanyZoneZipSelect } from './featureComponents/CompanyPane/CompanyZoneZipSelect/CompanyZoneZipSelect';
import { StApiCustomerPane } from './featureComponents/CustomerPane/StApiCustomerPane';
import {
  PhoneCallContext,
  selectAppointmentBookingPaneOpen,
  setAppointmentBookingPaneOpen,
  setHeldAppointmentContext,
  setPartialPhoneCallContext,
  setPhoneCallNonFieldErrors,
  switchDifferentTenantCompany
} from './StApiPhoneCall.slice';

const visibleSx = { visibility: 'visible', width: '100%', height: '100%' } as const;
const hiddenSx = { visibility: 'hidden', width: '0px', height: '0px' } as const;

type StApiPhoneCallProps = {
  companyContext: StApiCompanyContext;
  phoneCallURLState: PhoneCallURLState;
  mappedCall: ServiceTitanMappedCall | null;
  aiCall: AiCallForCallPopDTO['aiCall'] | null;
  handleSwitchCompany: (companyId: string) => Promise<Company | null>;
};

export const StApiPhoneCall = (props: StApiPhoneCallProps): React.ReactElement => {
  const { companyContext, phoneCallURLState, mappedCall, aiCall, handleSwitchCompany } = props;

  const api = useContext(ApiContext);
  const { enqueueSnackbar } = useOurSnackbar();
  const queryClient = useQueryClient();

  const dispatch = useAppDispatch();
  // Hide customer pane if Appointment Booking is open
  const appointmentBookingPaneOpen = useAppSelector(selectAppointmentBookingPaneOpen);

  const store = useAppStore();

  // Hoist the appointmentWindowState to this component, and re-initialize on top of other panes
  const [bookingState, setBookingState] = useImmer<BookingState>(createBookingState());

  const initializeBooking = useRef<boolean>(true);

  const handleSwitchStApiCompany = async (companyId: string) => {
    logDebug('// - Company Switch Handler ST Api V2 - //');

    const rootState = store.getState();

    const { heldAppointmentContext, customerContext, phoneCallContext } = rootState.stApiPhoneCallSlice;

    if (heldAppointmentContext.heldAppointment) {
      dispatch(setHeldAppointmentContext({ heldAppointment: undefined }));
      dispatch(setPhoneCallNonFieldErrors([]));
      api.appointments.hardDelete(heldAppointmentContext.heldAppointment.appointment.id).catch((err) => {
        reportException(err, { extra: { heldAppointment: heldAppointmentContext.heldAppointment, companyContext } });
      });
      enqueueSnackbar(`Company Switched: Appointment deleted.`);
    }

    const newCompany = await handleSwitchCompany(companyId);
    if (!newCompany || !isServiceTitanApiCompany(newCompany)) return;

    // close appointmentBookingPane and trigger re-init
    if (appointmentBookingPaneOpen) dispatch(setAppointmentBookingPaneOpen(false));
    setBookingState(createBookingState());
    initializeBooking.current = true;

    /*
     * NOTE: customerProviderId is not guaranteed to be Unique across companies think ST Instances with Integer IDs
     * So we should only let this cache live in the scope of a single company, with the exception of the case that
     * both companies have the same tenantId
     */
    if (
      newCompany.dataProvider.providerOptions.tenantId !== companyContext.company.dataProvider.providerOptions.tenantId
    ) {
      dispatch(switchDifferentTenantCompany(newCompany));
    } else {
      const updates: Partial<PhoneCallContext> = {
        trade: getTradeForNewCompany(phoneCallContext.trade, newCompany),
        assignedTech: undefined
      };

      /* Auto-select a relate calendar appointment if it is associated with the selected ST appointment */
      if (customerContext && phoneCallContext.previousServiceTitanAppointment) {
        const options = {
          params: { providerCustomerId: customerContext.customer.providerId, companyId: newCompany.id },
          timezone: newCompany.timezone
        };

        const upcomingCalendarAppointments = await queryClient.ensureQueryData({
          queryKey: api.appointments.customerUpcomingAppointmentsQueryKey(options),
          queryFn: () => api.appointments.customerUpcomingAppointments(options)
        });

        const previousCalendarAppointment = upcomingCalendarAppointments?.find(
          (appointment) =>
            appointment?.job?.providerData?.providerAppointmentId ===
            phoneCallContext.previousServiceTitanAppointment?.providerId
        );

        updates.previousAppointment = previousCalendarAppointment;
      }

      dispatch(setPartialPhoneCallContext({ updates }));
    }
  };

  const [tabIndex, setTabIndex] = useState(1); // default to customer pane shown if in tabbed view
  const [isSaving, setIsSaving] = useState(false);

  // If 'lg' or smaller, render with customer and company tabs
  const twoTabView = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  // If 'md' or smaller, customer, call, and company are all tabbed
  const threeTabView = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const generateCallDetailsTabSx = (): SxProps<Theme> => {
    if (threeTabView) return { width: '100%', height: '100%' };
    if (twoTabView) return { width: '20%', minWidth: '330px', height: '100%' };
    return { width: '20%', height: '100%', minWidth: '330px' };
  };

  // customer/company have same width/height
  const generateCustomerAndCompanyTabSx = (): SxProps<Theme> => {
    if (threeTabView) return { width: '100%', height: '100%' };
    if (twoTabView) return { width: '80%', height: '100%' };
    return { width: '40%', height: '100%' };
  };

  /**
   * Which pane the AppointmentBooking should overly (if it is open)
   * - AppointmentBooking on top of Call Details if in single pane/three-tab mode & call details is selected tab
   * - AppointmentBooking on top of Company if in either tabbed mode and company is selected tab
   * - Otherwise... overlay on top of customer
   */
  const overlayOnCall = appointmentBookingPaneOpen && threeTabView && tabIndex === 0;
  const overlayOnCompany = appointmentBookingPaneOpen && (threeTabView || twoTabView) && tabIndex === 2;
  const overlayOnCustomer = appointmentBookingPaneOpen && !overlayOnCall && !overlayOnCompany;

  // We want to auto-select 'customer' if we had 'call' selected and then left the breakpoint width
  useEffect(() => {
    if (twoTabView && !threeTabView && tabIndex === 0) setTabIndex(1);
  }, [twoTabView, threeTabView, tabIndex]);

  return (
    <Box sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <BackdropSpinner open={isSaving} />
      <Stack direction={'row'} spacing={0} sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <TabPanel currentIndex={tabIndex} index={0} alwaysDisplay={!threeTabView} sx={generateCallDetailsTabSx()}>
          {/* NOTE - the Call Details Pane generates its footer if we are not in two tab view */}
          <ErrorBoundary
            beforeCapture={(scope) => {
              scope.setTag('pane', 'Call Details Pane - Outer');
            }}
            fallback={({ resetError }) => (
              <ErrorBoundaryFallback
                title='Error Displaying Call Details'
                onRefresh={() => {
                  // NOTE - we do not have a way to 'rescue' state here without a serious refactor!
                  resetError();
                }}
              />
            )}>
            <CallDetailsPane
              phoneCallURLState={phoneCallURLState}
              mappedCall={mappedCall}
              aiCallId={aiCall?.id}
              companyContext={companyContext}
              bookingState={bookingState}
              setBookingState={setBookingState}
              sx={overlayOnCall ? hiddenSx : visibleSx}
              showFooter={!twoTabView || threeTabView}
              onSetIsSaving={setIsSaving}
            />
          </ErrorBoundary>
          {overlayOnCall && (
            <AppointmentBookingPane
              companyContext={companyContext}
              bookingState={bookingState}
              setBookingState={setBookingState}
              initializeBooking={initializeBooking}
              sx={visibleSx}
            />
          )}
        </TabPanel>
        <VerticalDivider />
        <TabPanel
          currentIndex={tabIndex}
          index={1}
          alwaysDisplay={!twoTabView && !threeTabView}
          sx={generateCustomerAndCompanyTabSx()}>
          {overlayOnCustomer && (
            <AppointmentBookingPane
              companyContext={companyContext}
              bookingState={bookingState}
              setBookingState={setBookingState}
              initializeBooking={initializeBooking}
              sx={visibleSx}
            />
          )}
          <StApiCustomerPane
            phoneCallURLState={phoneCallURLState}
            company={companyContext.company}
            sx={overlayOnCustomer ? hiddenSx : visibleSx}
            hideAllTabs={twoTabView && tabIndex !== 1}
          />
        </TabPanel>
        <VerticalDivider />
        <TabPanel
          currentIndex={tabIndex}
          index={2}
          alwaysDisplay={!twoTabView && !threeTabView}
          sx={generateCustomerAndCompanyTabSx()}>
          <CompanyPane
            companyContext={companyContext}
            phoneCallURLState={phoneCallURLState}
            sx={overlayOnCompany ? hiddenSx : visibleSx}
            hideAllTabs={twoTabView && tabIndex !== 2}
            zipZoneOptions={{
              ZipZoneSelectComponent: CompanyZoneZipSelect
            }}
            aiCall={aiCall}
            handleSwitchCompany={handleSwitchStApiCompany}
          />
          {overlayOnCompany && (
            <AppointmentBookingPane
              companyContext={companyContext}
              bookingState={bookingState}
              setBookingState={setBookingState}
              initializeBooking={initializeBooking}
              sx={visibleSx}
            />
          )}
        </TabPanel>
      </Stack>
      {twoTabView && !threeTabView && !appointmentBookingPaneOpen && (
        <TwoTabFooter callDetailsWidth='20%' tabIndex={tabIndex} setTabIndex={setTabIndex} />
      )}
      {threeTabView && !appointmentBookingPaneOpen && <ThreeTabFooter tabIndex={tabIndex} setTabIndex={setTabIndex} />}
    </Box>
  );
};
